import { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Ticket, 
  Search, 
  Code2, 
  GitBranch, 
  Shield, 
  Menu,
  X,
  BarChart3,
  Settings as SettingsIcon,
  Sparkles,
  CheckSquare,
  Rocket,
  AlertTriangle,
  BookOpen,
  FileText,
  Network,
  Target,
  ChevronRight,
  ChevronDown,
  LogOut,
  History,
  Zap
} from 'lucide-react';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// Import pages
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Tickets from '@/pages/Tickets';
import TicketDetail from '@/pages/TicketDetail';
import EnhancedTicketDetail from '@/pages/EnhancedTicketDetail';
import ErrorTicketDetail from '@/pages/ErrorTicketDetail';
import TicketDetailRouter from '@/pages/TicketDetailRouter';
import DiscoveryEngine from '@/pages/DiscoveryEngine';
import VibeCodingStudio from '@/pages/VibeCodingStudio';
import VibeChatWorkspace from '@/pages/VibeChatWorkspace';
import CodeEditor from '@/pages/CodeEditor';
import Governance from '@/pages/Governance';
import DeploymentStudio from '@/pages/DeploymentStudio';
import ErrorResolution from '@/pages/ErrorResolution';
import Analytics from '@/pages/Analytics';
import Settings from '@/pages/Settings';
import AgentStudio from '@/pages/AgentStudio';
import ApprovalWorkflow from '@/pages/ApprovalWorkflow';
import Documentation from '@/pages/Documentation';
import Reports from '@/pages/Reports';
import TriggerWorkflow from '@/pages/TriggerWorkflow';

// Navigation categories (full parity with juno-halo frontend layout).
const navCategories = [
  {
    label: 'Core Workflow',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard' },
        { path: '/trigger-workflow', icon: Zap, label: 'Trigger Workflow', testId: 'nav-trigger-workflow' },
      { path: '/tickets', icon: Ticket, label: 'Tickets', testId: 'nav-tickets' },
    ]
  },
  {
    label: 'AI & Automation',
    items: [
      { path: '/agent-studio', icon: Sparkles, label: 'Agent Studio', testId: 'nav-agent-studio' },
      { path: '/vibe-studio', icon: Code2, label: 'Vibe Coding Studio', testId: 'nav-vibe-studio' },
      { path: '/vibe-studio/chat', icon: History, label: 'Chat history', testId: 'nav-chat-history' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/discovery', icon: Search, label: 'Discovery Engine', testId: 'nav-discovery' },
      { path: '/deployment', icon: Rocket, label: 'Deployment Studio', testId: 'nav-deployment' },
      { path: '/errors', icon: AlertTriangle, label: 'Error Resolution', testId: 'nav-errors' },
      { path: '/approvals', icon: CheckSquare, label: 'Approvals', testId: 'nav-approvals' },
    ]
  },
  {
    label: 'Insights',
    items: [
      { path: '/analytics', icon: BarChart3, label: 'Analytics', testId: 'nav-analytics' },
      { path: '/reports', icon: FileText, label: 'Reports', testId: 'nav-reports' },
      { path: '/documentation', icon: BookOpen, label: 'Documentation', testId: 'nav-documentation' },
    ]
  },
  {
    label: 'Configuration',
    items: [
      { path: '/settings', icon: SettingsIcon, label: 'Settings', testId: 'nav-settings' },
    ]
  }
];

function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState(
    navCategories.reduce((acc, cat) => ({ ...acc, [cat.label]: true }), {})
  );

  const toggleCategory = (label) => {
    setExpandedCategories(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full bg-slate-900 text-white z-50 transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:z-0
          w-64 flex flex-col
        `}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">Halo</h1>
              <p className="text-xs text-slate-400 mt-1">SAP AI Customization Engine</p>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navCategories.map((category) => {
            const isExpanded = expandedCategories[category.label];
            
            return (
              <div key={category.label} className="mb-4">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.label)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-200 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span>{category.label}</span>
                    {category.badge && (
                      <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                        {category.badge}
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                
                {/* Category Items */}
                {isExpanded && (
                  <div className="mt-1 space-y-1">
                    {category.items.map((item) => {
                      const Icon = item.icon;
                      const isActive =
                        item.path === '/vibe-studio'
                          ? location.pathname === '/vibe-studio'
                          : location.pathname === item.path ||
                            location.pathname.startsWith(`${item.path}/`);
                      
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsOpen(false)}
                          className={`
                            flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm
                            ${isActive 
                              ? 'bg-blue-600 text-white' 
                              : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                            }
                          `}
                          data-testid={item.testId}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <UserSection />
        </div>
      </aside>
    </>
  );
}

function UserSection() {
  const { user, logout, role, isAdmin } = useAuth();
  const roleLabel =
    role === "admin" ? "Administrator" : role === "user" ? "User" : "Signed in";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 p-2 bg-slate-800 rounded-lg">
        <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
          {(user?.email || user?.username || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{user?.email || user?.username || 'User'}</div>
          <div className={`text-xs ${isAdmin ? 'text-amber-400' : 'text-slate-400'}`}>{roleLabel}</div>
        </div>
      </div>
      <button
        onClick={logout}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        data-testid="logout-btn"
      >
        <LogOut className="h-4 w-4" />
        <span>Sign out</span>
      </button>
      <div className="text-xs text-slate-500 pt-2 border-t border-slate-800">
        <div>Version 1.0.0</div>
        <div className="mt-1 flex items-center gap-1">
          <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
          <span>All systems operational</span>
        </div>
      </div>
    </div>
  );
}

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-600 hover:text-slate-900"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-slate-900">Multi-Agent Orchestration Platform</span>
            </div>
          </div>
          
          {/* Powered by Logo */}
          <a 
            href="https://www.resolvetech.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity" 
            data-testid="powered-by-logo"
          >
            <span className="text-sm text-slate-500 font-medium">Powered by</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-customizer-1/artifacts/ziz1wre7_rtslogo-with-name-transparent-300-ppi-RGB.png" 
              alt="Resolve Tech Solutions" 
              className="h-8 object-contain"
            />
          </a>
        </header>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <NotificationProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/trigger-workflow" element={<TriggerWorkflow />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/:id" element={<TicketDetailRouter />} />
            <Route path="/tickets-old/:id" element={<EnhancedTicketDetail />} />
            <Route path="/agent-studio" element={<AgentStudio />} />
            <Route path="/discovery" element={<DiscoveryEngine />} />
            <Route path="/vibe-studio/past-sessions" element={<Navigate to="/vibe-studio/chat" replace />} />
            <Route path="/vibe-studio/chat" element={<VibeChatWorkspace />} />
            <Route path="/vibe-studio" element={<VibeCodingStudio />} />
            <Route path="/vibe-studio/:ticketId" element={<CodeEditor />} />
            <Route path="/approvals" element={<ApprovalWorkflow />} />
            <Route path="/deployment" element={<DeploymentStudio />} />
            <Route path="/errors" element={<ErrorResolution />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </Layout>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </NotificationProvider>
  );
}

function AppWithAuth() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return <App />;
}

function AppWrapper() {
  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}

export default AppWrapper;
